import {useContext, useEffect} from 'react';
import BryntumContext from "../context/bryntumContext.jsx";
import { ObjectHelper, StateProvider } from "@bryntum/schedulerpro";
import useViews from "./useViews.jsx";
import { TIMELINE_CONG_KEYS, VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { PLUGINS_KEYS } from "../../other/constants/PluginKeys.jsx";

// This function will see if there is a change in columns structure of grid for instance, columns added or removed
// if any of the case, then it will make those changes in currently selected view record viewstate
// doing so will help in unnecessary change detection when making non view related changes, such as, change width of column
const consolidateColumnsCodeChangesInViewState = (gridConfig, selectedView, viewType, viewsStore) => {
  const { columns: codedColumns } = gridConfig;
  const { columns: viewStateColumns } = selectedView.isDefaultView ? selectedView.viewState.customViewState?.[viewType] || {} : selectedView.viewState[viewType];

  if (!viewStateColumns?.length || !codedColumns?.length) return;

  // Stage1: find removed and newly added columns

  const removedColumns = viewStateColumns.filter((viewStateCol) => !codedColumns.some((codedCol) => codedCol.id === viewStateCol.id));
  const addedColumns = codedColumns.filter((codedCol) => !viewStateColumns.some((viewStateCol) => codedCol.id === viewStateCol.id));

  if (!removedColumns.length && !addedColumns.length) return;

  // Stage2: remove old columns and add newly added columns at exact position in selected view state columns

  const updatedColumns = viewStateColumns.filter((viewStateCol) => !removedColumns.some((removedCol) => removedCol.id === viewStateCol.id));

  addedColumns.forEach((addedCol) => {
    const newColIdx = codedColumns.findIndex(codedCol => codedCol.id === addedCol.id);
    const addedColWithFields = Object.keys(viewStateColumns[0]).reduce((acc, field) => {
      acc[field] = addedCol[field];
      return acc;
    }, {});
    updatedColumns.splice(newColIdx, 0, addedColWithFields);
  });

  // Stage3: apply changes on selected record
  const viewState = { ...selectedView.viewState };
  if (selectedView.isDefaultView) {
    viewState.customViewState = {
      ...viewState.customViewState,
      [viewType]: { ...viewState.customViewState[viewType], columns: updatedColumns }
    }
  } else {
    viewState[viewType] = { ...viewState[viewType], columns: updatedColumns }
  }
  viewsStore.applyChangeset({
    updated: [{
      id: selectedView.id,
      viewState
    }]
  })
}

const consolidateAdditionalFieldsInViewState = ({ statefulWidget, selectedView, viewType, pageType, viewsStore }) => {
  // adding isPhantom check as, on saving a custom view the view gets selected as soon as it created in that meantime the view may have not saved to the backend
  if (!selectedView.isPhantom) {
    const isScheduleView = [VIEW_TYPES.planView, VIEW_TYPES.schedulerView].includes(pageType) && statefulWidget.isSchedulerPro;
    if (isScheduleView) {
      const viewState = selectedView.isDefaultView ? (selectedView.viewState.customViewState || {}) : selectedView.viewState;
      const viewStateForType = viewState[viewType];
      if (viewStateForType) {
        const featuresToAdd = statefulWidget.state.features || {};
        const isKeyAddedOrRemovedInTimelineConfig = viewStateForType?.features?.[PLUGINS_KEYS.swivuTimelineConfigurator]
          ? Object.keys(viewStateForType.features[PLUGINS_KEYS.swivuTimelineConfigurator]).length !== Object.values(TIMELINE_CONG_KEYS).length: false;
        if (
          !viewStateForType.features ||
          !viewStateForType.features.timeRanges ||
          !viewStateForType.features[PLUGINS_KEYS.swivuTimelineConfigurator] ||
          isKeyAddedOrRemovedInTimelineConfig
        ) {
          const updatedViewStateObjectForType = ObjectHelper.clone(viewState[viewType]);
          if (!updatedViewStateObjectForType.features) updatedViewStateObjectForType.features = {};
          if (!updatedViewStateObjectForType.features.timeRanges) updatedViewStateObjectForType.features.timeRanges = featuresToAdd.timeRanges;
          if (!updatedViewStateObjectForType.features[PLUGINS_KEYS.swivuTimelineConfigurator]) {
            updatedViewStateObjectForType.features[PLUGINS_KEYS.swivuTimelineConfigurator] = featuresToAdd[PLUGINS_KEYS.swivuTimelineConfigurator];
          } else if (isKeyAddedOrRemovedInTimelineConfig) {
            // if there is any key added or removed from Timeline Config then make sure to softly remove it from the existing saved view
            updatedViewStateObjectForType.features[PLUGINS_KEYS.swivuTimelineConfigurator] = Object.fromEntries(
              Object.values(TIMELINE_CONG_KEYS).map((key) => [key, viewState[viewType].features[PLUGINS_KEYS.swivuTimelineConfigurator][key]])
            );
          }
          const viewStateToApply = { ...selectedView.viewState };
          if (selectedView.isDefaultView) {
            viewStateToApply.customViewState[viewType] = updatedViewStateObjectForType;
          } else {
            viewStateToApply[viewType] = updatedViewStateObjectForType;
          }
          viewsStore.applyChangeset({
            updated: [{
              id: selectedView.id,
              viewState: viewStateToApply
            }]
          });
        }
      }
    }
  }
}

const enableStateTrackingToViews = (viewManager, statefulWidget, viewType) => {
  // Don't know why it was added, but disabling resetDefaultState() help in ui flickering related issues such as subgrid width expands too much on when schedule custom view dismisses
  // see issue => https://admin.dev.swivu.com/swivu-engineering-rjv68i/scheduler?task=60348&tab=general
  // One of the issue I noticed that if I have a different preset selected in custom view, and then I switch to schedule view then the default view inherits that custom view timeline preset

  // Re-enabled on 9 May 2025 as switching view from custom view to default cause that view state to persist into the customViewState object see viewManager
  // statefulWidget.resetDefaultState();

  // those stores that don't have a correct modelClass will provide their actual model in `swivuModelClassForMapping` field
  const modelClass = statefulWidget.store.swivuModelClassForMapping || statefulWidget.store.modelClass;
  // Since we have a fallback inside `getViewState` mechanism now that updates states in default view record if empty
  // Therefore fetching state first before applying sorting.
  const viewState = viewManager.getViewState({ viewType, initialDefaultState: statefulWidget.defaultState, storeModelClass: modelClass });
  // when switching view, we have to revert the order of records to the original state otherwise the next sort that will be applied will depend on the order of previous view state (if the previous state has any sort or group in that)
  statefulWidget.state = {
    store: {
      sorters: [{
        field: "order",
        ascending: true,
      }]
    }
  }

  const vsProvider = new StateProvider({ storage: "memory" }); // default storage is "localstorage"
  statefulWidget.state = viewState;
  statefulWidget.stateProvider = vsProvider;
  return vsProvider.on("set", ({ value: state, was: oldState }) => {
    if (oldState) { // if we do not have an old state means view just load hence preventing the unnecessary change check operation, which may sometimes cause "Update" popup to appear when view is selected
      // Due to non-working records scheduler pro's default state is dynamic that's why we created a getter for default state
      const widgetDefaultState = statefulWidget.isSchedulerPro ? statefulWidget.swivuSchedulerDefaultState : statefulWidget.defaultState;
      viewManager.onViewStateChange({ oldState, viewType, state, initialDefaultState: widgetDefaultState, storeModelClass: modelClass });
    }
  });
};

const useEnableViewTracking = (statefulWidget, viewType, isRefInit, pageType, onAfterViewAssigned) => {
  const { viewManager, project } = useContext(BryntumContext);
  const viewManagerState = useViews();

  useEffect(() => {
    if (isRefInit && statefulWidget && viewManagerState?.selectedView?.viewType === (pageType || viewType)) {
      if (!viewManager.pageViewsRef) {
        viewManager.pageViewsRef = {};
      }
      viewManager.pageViewsRef[viewType] = statefulWidget.current.instance;
      consolidateColumnsCodeChangesInViewState(statefulWidget.current.instance.defaultState, viewManagerState.selectedView, viewType, project.viewsStore);
      consolidateAdditionalFieldsInViewState({ statefulWidget: statefulWidget.current.instance, selectedView: viewManagerState.selectedView, pageType, viewType, viewsStore: project.viewsStore });
      const viewStateListenerDestroy = enableStateTrackingToViews(viewManager, statefulWidget.current.instance, viewType);
      if (onAfterViewAssigned) onAfterViewAssigned(statefulWidget);
      return () => {
        viewManager.pageViewsRef = null;
        viewStateListenerDestroy();
      }
    }
  }, [viewManager, viewManagerState, project, isRefInit]);
};

export default useEnableViewTracking;
